import { Search, X, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiFilter } from "@/components/catalog/MultiFilter";
import { FILTERS, SORTS, optionsFor, emptyState } from "@/lib/catalogFilters";

const SCORES = [0, 7, 8, 9];

function ActiveChips({ type, state, setState }) {
  const chips = FILTERS[type].flatMap((f) => (state.sel[f.key] || []).map((v) => ({ f, v })));
  const remove = (f, v) => setState({ ...state, sel: { ...state.sel, [f.key]: state.sel[f.key].filter((x) => x !== v) } });
  if (!chips.length && !state.minScore) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
      {chips.map(({ f, v }) => (
        <button key={f.key + v} onClick={() => remove(f, v)} data-testid={`chip-${f.key}-${v}`}
          className="inline-flex items-center gap-1 text-xs font-semibold bg-orange/10 text-navy pl-3 pr-2 py-1.5 rounded-full hover:bg-orange/20 transition-colors">
          <span className="text-muted-foreground">{f.label}:</span> {v} <X className="h-3 w-3" />
        </button>
      ))}
      {state.minScore > 0 && (
        <button onClick={() => setState({ ...state, minScore: 0 })} data-testid="chip-score"
          className="inline-flex items-center gap-1 text-xs font-semibold bg-orange/10 text-navy pl-3 pr-2 py-1.5 rounded-full hover:bg-orange/20">
          Score ≥ {state.minScore} <X className="h-3 w-3" />
        </button>
      )}
      <button onClick={() => setState({ ...emptyState(), sort: state.sort })} className="text-xs font-bold text-orange hover:underline ml-1" data-testid="filters-reset">
        Reset all
      </button>
    </div>
  );
}

export function CatalogToolbar({ type, items, state, setState }) {
  const set = (patch) => setState({ ...state, ...patch });
  return (
    <div className="space-y-3 mb-6">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid={`search-${type}`} value={state.q} onChange={(e) => set({ q: e.target.value })}
            placeholder={type === "school" ? "Search schools, cities…" : "Search programs, schools…"} className="pl-10 h-11 rounded-xl bg-white" />
        </div>
        <Select value={state.sort} onValueChange={(v) => set({ sort: v })}>
          <SelectTrigger className="h-11 rounded-xl lg:w-52 bg-white" data-testid={`sort-${type}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORTS[type].map((s) => <SelectItem key={s.key} value={s.key} data-testid={`sort-option-${s.key}`}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
        {FILTERS[type].map((f) => (
          <MultiFilter key={f.key} filter={f} options={optionsFor(items, f)} selected={state.sel[f.key]}
            onChange={(v) => set({ sel: { ...state.sel, [f.key]: v } })} />
        ))}
        {type === "school" && items.some((i) => i.score) && (
          <Select value={String(state.minScore)} onValueChange={(v) => set({ minScore: Number(v) })}>
            <SelectTrigger className="h-11 w-36 shrink-0 rounded-xl bg-white text-sm font-semibold text-navy" data-testid="filter-score"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SCORES.map((s) => <SelectItem key={s} value={String(s)} data-testid={`score-option-${s}`}>{s ? `Score ≥ ${s}` : "Any score"}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <ActiveChips type={type} state={state} setState={setState} />
    </div>
  );
}
